import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useRef, useMemo, } from 'react';
import { Navigate } from "react-router-dom";
import { Box, Typography, } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDesignationWiseUsers, fetchClientDetails, selectDesignationWiseUsers, } from '../../redux/clientDataSlice';
import { fetchClientListWithIcp, bulkUpdateClientIcpFields, selectClientListWithIcp, selectClientListWithIcpLoading, selectClientListWithIcpPagination, selectClientListWithIcpCounts, } from '../../redux/activeClientListSlice';
import { useProjectOptions } from '../../context/ProjectOptionsContext';
import { AM_DESIGNATIONS, collectUsersByDesignations } from '../../utils/designationFilters';
import PageContainer from '../../common_components/PageContainer';
import PagesCommonHeader from '../../common_components/PagesCommonHeader';
import LastUpdatedInfo from '../../common_components/LastUpdatedInfo';
import Paper from '@mui/material/Paper';
import CommonTable from '../../common_components/CommonTable';
import Pagination from '../../common_components/Pagination';
import TableToolbar from '../../common_components/TableToolbar';
import CommonTabs from '../../common_components/CommonTabs';
import AppButton from '../../common_components/AppButton';
import { Dropdown, SearchInput, ExportButton } from '../../common_components/FormInputs';
import AddEditClientDrawer from '../ClientList/AddEditDrawer/AddEditClientDrawer';
import ExpandedRow from './components/ExpandedRow';
import AddNetworkDrawer from './components/AddNetworkDrawer';
import CommonChip from '../../common_components/CommonChip';
import HoverTooltip from '../../common_components/hoverOverlay/HoverTooltip';
import { ROUTES } from "../../routes/routes.constants";
import '../../App.css';
import { getAvatarColor, TYPE_LABEL_MAP } from './shared';
import exportActiveClientList from './exportActiveClientList';
import useToast from '../../common_components/Toast/useToast';
import { selectPermissions } from '../../redux/authSlice';
import { triggerRefresh } from '../../redux/refreshSlice';
import { EXPORT_BATCH_SIZE } from '../../utils/exportHelpers';
const CLIENT_PAGINATION = { DEFAULT_PAGE: 1, LIMIT: 20 };
function formatDate(dateStr) {
    if (!dateStr)
        return '';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime()))
            return '';
        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }
    catch {
        return '';
    }
}
function formatLastUpdated() {
    return new Date().toISOString();
}
/* ─────────────────────────── main page ─────────────────────────── */
export default function ActiveClientList() {
    const dispatch = useDispatch();
    const toast = useToast();
    const hasFetchedUsers = useRef(false);
    /* ── redux ── */
    const token = useSelector((s) => s.auth?.token);
    const permissions = useSelector(selectPermissions);
    const clients = useSelector(selectClientListWithIcp);
    const counts = useSelector(selectClientListWithIcpCounts);
    const { categoryOptions: categoryOptionsFromApi } = useProjectOptions();
    const { totalEntries, totalPages, page: currentPage } = useSelector(selectClientListWithIcpPagination);
    const loading = useSelector(selectClientListWithIcpLoading);
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    /* ── filters ── */
    const [status, setStatus] = useState('active');
    const [searchText, setSearchText] = useState('');
    const [type, setType] = useState([]);
    const [selectedLeadAM, setSelectedLeadAM] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedContractTerm, setSelectedContractTerm] = useState('');
    const [page, setPage] = useState(CLIENT_PAGINATION.DEFAULT_PAGE);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showFilterRow, setShowFilterRow] = useState(false);
    const [activeFilterColumnId, setActiveFilterColumnId] = useState(null);
    const [columnFilters, setColumnFilters] = useState({});
    const [columnOrder, setColumnOrder] = useState(null);
    const [selectedRows, setSelectedRows] = useState(new Set());
    /* ── table features ── */
    const [sortConfig, setSortConfig] = useState({
        columnId: null,
        direction: 'asc',
    });
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({
        left: [],
        right: [],
    });
    const [exporting, setExporting] = useState(false);
    /* ── ICP edit draft state for Alias/Sample Status/Brand Description (per client) ── */
    const [icpDrafts, setIcpDrafts] = useState({});
    const [icpSaving, setIcpSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    // ── permissions for Active Client module (same pattern as Holiday Calendar) ──
    const hasPermission = permissions?.active_clients?.actions?.includes('Manage Active Clients') ?? false;
    const hasModuleAccess = permissions?.active_clients?.access !== false &&
        permissions?.active_client_list?.access !== false &&
        permissions?.activeClientList?.access !== false;
    if (!hasModuleAccess) {
        return _jsx(Navigate, { to: ROUTES.notFound.path, replace: true });
    }
    // ── refresh data permission ──
    const canRefreshData = permissions?.active_clients?.refresh ?? false;
    /* ── edit drawer ── */
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [clientEditData, setClientEditData] = useState(null);
    /* ── add drawer ── */
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    /* ── add network drawer ── */
    const [networkDrawerOpen, setNetworkDrawerOpen] = useState(false);
    const [networkClient, setNetworkClient] = useState(null);
    /* ─── init: fetch designation users ─── */
    useEffect(() => {
        if (hasFetchedUsers.current)
            return;
        hasFetchedUsers.current = true;
        dispatch(fetchDesignationWiseUsers());
    }, [dispatch]);
    useEffect(() => {
        setPage(CLIENT_PAGINATION.DEFAULT_PAGE);
    }, [status, searchText, type, selectedLeadAM, selectedCategory, selectedContractTerm]);
    /* Map frontend column id to API sortBy param */
    const columnIdToSortBy = useMemo(() => ({
        name: 'name',
        category: 'icp.category',
        type: 'type',
        start_date: 'start_date',
        termination_date: 'termination_date',
        mrr: 'icp.contract_mrr',
        commission: 'icp.contract_commission_rate',
    }), []);
    /* ─── param builder ─── */
    const buildParams = useCallback(() => {
        const params = new URLSearchParams();
        params.append('page', String(page));
        params.append('limit', String(CLIENT_PAGINATION.LIMIT));
        params.append('status', status);
        const search = searchText;
        if (search)
            params.append('searchText', search);
        if (selectedLeadAM)
            params.append('am_id', selectedLeadAM);
        if (selectedCategory)
            params.append('category', selectedCategory);
        if (selectedContractTerm)
            params.append('contract_term', selectedContractTerm);
        type.forEach((t) => params.append('type', t));
        if (sortConfig?.columnId) {
            const sortBy = columnIdToSortBy[sortConfig.columnId] ?? sortConfig.columnId;
            params.append('sortBy', sortBy);
            params.append('sortOrder', sortConfig.direction ?? 'asc');
        }
        return params;
    }, [page, status, searchText, selectedLeadAM, selectedCategory, selectedContractTerm, type, sortConfig, columnIdToSortBy]);
    /* ─── fetch ─── */
    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const payload = await dispatch(fetchClientListWithIcp({ params: buildParams() })).unwrap();
                if (!isMounted)
                    return;
                setLastUpdated(payload?.lastUpdated ?? null);
            }
            catch {
                // keep lastUpdated as-is
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [dispatch, buildParams]);
    const refetchClientList = useCallback(() => {
        dispatch(fetchClientListWithIcp({ params: buildParams() }))
            .unwrap()
            .then((payload) => {
            setLastUpdated(payload?.lastUpdated ?? null);
        })
            .catch(() => {
            // no-op
        });
    }, [dispatch, buildParams]);
    const handleSyncRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            const resultAction = await dispatch(triggerRefresh({ moduleKey: 'active_clients' }));
            if (triggerRefresh.fulfilled.match(resultAction)) {
                const payload = resultAction.payload;
                if (payload?.lastUpdated)
                    setLastUpdated(payload.lastUpdated);
                toast.success('Active client updated successfully');
            }
            else {
                toast.error('Failed to update active client');
            }
        }
        catch {
            toast.error('Sync failed. Please try again.');
        }
        finally {
            setRefreshing(false);
        }
        dispatch(fetchClientListWithIcp({ params: buildParams() }));
    }, [dispatch, buildParams, toast]);
    const handleSearchChange = useCallback((text) => {
        setSearchText(text);
        setPage(1);
    }, []);
    const handleColumnFilterChange = useCallback((columnId, filterValue) => {
        setColumnFilters((prev) => {
            const next = { ...prev };
            if (filterValue)
                next[columnId] = filterValue;
            else
                delete next[columnId];
            return next;
        });
        setPage(1);
    }, []);
    const handleFilterRowToggle = useCallback((payload = null) => {
        if (!payload || payload?.mode === 'all') {
            setActiveFilterColumnId(null);
            setShowFilterRow((prev) => (payload?.open !== undefined ? payload.open : !prev));
            return;
        }
        if (payload?.mode === 'single' && payload?.columnId) {
            if (payload?.open === false) {
                setActiveFilterColumnId(null);
                setShowFilterRow(false);
                return;
            }
            setActiveFilterColumnId(payload.columnId);
            setShowFilterRow(true);
        }
    }, []);
    const handleColumnOrderChange = useCallback((newOrder) => {
        setColumnOrder(newOrder);
    }, []);
    const columnFilterConfig = useMemo(() => ({
        enable: true,
        columns: {
            name: { type: 'search' },
            type: {
                type: 'multi-select', // 👈 IMPORTANT
                options: [
                    { label: 'Affiliate', value: 'affiliate' },
                    { label: 'Paid Media', value: 'paid_media' },
                    { label: 'AEO', value: 'aeo' },
                ],
            },
        },
    }), []);
    /* ─── dropdown options ─── */
    const accountManagers = useMemo(() => collectUsersByDesignations(designationWiseUsers?.designationMap, AM_DESIGNATIONS), [designationWiseUsers]);
    const amOptions = useMemo(() => [
        { value: '', label: 'All' },
        ...accountManagers.map((u) => ({
            value: u._id,
            label: `${u.first_name} ${u.last_name}`,
        })),
    ], [accountManagers]);
    const typeOptions = useMemo(() => [
        { value: 'paid_media', label: 'Paid Media' },
        { value: 'affiliate', label: 'Affiliate' },
        { value: 'aeo', label: 'AEO' },
    ], []);
    const handleTypeChange = useCallback((val) => {
        setType(val);
        setPage(1);
    }, [setType, setPage]);
    const categoryOptions = useMemo(() => {
        const apiOptions = Array.isArray(categoryOptionsFromApi)
            ? categoryOptionsFromApi
                .filter((o) => o && typeof o.value === 'string' && typeof o.label === 'string')
                .map((o) => ({ value: o.value, label: o.label }))
            : [];
        return [{ value: '', label: 'All' }, ...apiOptions];
    }, [categoryOptionsFromApi]);
    /* ─── display values for filter dropdowns ─── */
    const typeDisplayValue = useMemo(() => {
        let label = 'All';
        if (type.length === 1) {
            label = typeOptions.find((t) => t.value === type[0])?.label ?? 'All';
        }
        else if (type.length > 1) {
            label = type.map((t) => typeOptions.find((o) => o.value === t)?.label).filter(Boolean).join(', ');
        }
        return (_jsxs("span", { children: [_jsx("span", { style: { color: '#101828', fontWeight: 500 }, children: "Type:" }), ' ', _jsx("span", { style: { color: '#667085' }, children: label })] }));
    }, [type, typeOptions]);
    const categoryDisplayValue = useMemo(() => {
        const label = selectedCategory
            ? categoryOptions.find((o) => o.value === selectedCategory)?.label ?? 'All'
            : 'All';
        return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx("span", { style: { color: '#101828', fontWeight: 500 }, children: "Category" }), ' ', _jsx("span", { style: { color: '#667085', fontWeight: 400 }, children: label })] }));
    }, [selectedCategory, categoryOptions]);
    const contractTermOptions = useMemo(() => [
        { value: '', label: 'All' },
        { value: 'Monthly', label: 'Monthly' },
        { value: '3 Months', label: '3 Months' },
        { value: '6 Months', label: '6 Months' },
        { value: 'Yearly', label: 'Yearly' },
    ], []);
    const contractTermDisplayValue = useMemo(() => {
        const label = selectedContractTerm
            ? contractTermOptions.find((o) => o.value === selectedContractTerm)?.label ?? 'All'
            : 'All';
        return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx("span", { style: { color: '#101828', fontWeight: 500 }, children: "Contract Term" }), ' ', _jsx("span", { style: { color: '#667085', fontWeight: 400 }, children: label })] }));
    }, [selectedContractTerm, contractTermOptions]);
    const amDisplayValue = useMemo(() => {
        const found = accountManagers.find((u) => u._id === selectedLeadAM);
        const label = found ? `${found.first_name} ${found.last_name}` : 'All';
        return (_jsxs("span", { children: [_jsx("span", { style: { color: '#101828', fontWeight: 500 }, children: "Lead Account Manager:" }), ' ', _jsx("span", { style: { color: '#667085' }, children: label })] }));
    }, [selectedLeadAM, accountManagers]);
    /* ─── derived rows (apply optional client-side filters) ─── */
    const filteredClients = useMemo(() => {
        let data = [...clients];
        // 🔍 Name filter
        const nameFilter = (columnFilters?.name ?? '').toString().toLowerCase();
        if (nameFilter) {
            data = data.filter((c) => (c.name ?? '').toLowerCase().includes(nameFilter));
        }
        // 🔥 Type filter (MAIN FIX)
        if (columnFilters?.type) {
            const selectedTypes = Array.isArray(columnFilters.type)
                ? columnFilters.type
                : [columnFilters.type];
            data = data.filter((client) => {
                const clientTypes = Array.isArray(client.type)
                    ? client.type
                    : client.type
                        ? [client.type]
                        : [];
                return (clientTypes.length === selectedTypes.length &&
                    selectedTypes.every((t) => clientTypes.includes(t)));
            });
        }
        return data;
    }, [clients, columnFilters]);
    /* ─── table rows with stable id for selection/export ─── */
    const tableRows = useMemo(() => filteredClients.map((row) => ({ ...row, id: row._id })), [filteredClients]);
    const handleExportAllClients = useCallback(async () => {
        if (exporting)
            return;
        try {
            setExporting(true);
            // Build from allUsers (every designation), not just accountManagers.
            // Handler roles like Search/Email/Social/Design/OS are not account managers,
            // so limiting the map to accountManagers left those columns blank in the export.
            const accountUserMap = {};
            (designationWiseUsers?.allUsers ?? []).forEach((u) => {
                const full = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() ||
                    u.email ||
                    '';
                accountUserMap[u._id] = full;
            });
            const selectedIds = Array.from(selectedRows ?? new Set());
            if (selectedIds.length > 0) {
                // Export only selected rows using already-loaded data
                const selectedClients = tableRows.filter((c) => selectedIds.includes(c.id));
                if (!selectedClients.length) {
                    toast.error('No clients selected. Please select clients to export.');
                }
                else {
                    exportActiveClientList(selectedClients, accountUserMap);
                }
            }
            else {
                // ✅ Paginated batching: fetch EXPORT_BATCH_SIZE rows per request
                const baseParams = buildParams();
                let allClients = [];
                let currentPage = 1;
                let totalPages = 1;
                do {
                    const batchParams = new URLSearchParams(baseParams.toString());
                    batchParams.set('page', String(currentPage));
                    batchParams.set('limit', String(EXPORT_BATCH_SIZE));
                    const res = await dispatch(fetchClientListWithIcp({ params: batchParams })).unwrap();
                    const batch = res?.clients ?? [];
                    allClients = allClients.concat(batch);
                    const total = res?.total ?? 0;
                    totalPages = total > 0 ? Math.ceil(total / EXPORT_BATCH_SIZE) : 1;
                    currentPage++;
                } while (currentPage <= totalPages);
                // ✅ Exact Set Logic Filter: only clients that have EXACTLY the selected types
                if (type.length > 0) {
                    allClients = allClients.filter((client) => {
                        const clientTypes = Array.isArray(client.type)
                            ? client.type
                            : client.type
                                ? [client.type]
                                : [];
                        return (clientTypes.length === type.length &&
                            type.every((selectedType) => clientTypes.includes(selectedType)));
                    });
                }
                if (!allClients.length) {
                    toast.error('No clients available to export.');
                    return;
                }
                exportActiveClientList(allClients, accountUserMap);
            }
        }
        catch (error) {
            console.error('Export Active Client List failed', error);
            toast.error('Failed to export active client list. Please try again.');
        }
        finally {
            setExporting(false);
            dispatch(fetchClientListWithIcp({ params: buildParams() }));
        }
    }, [
        dispatch,
        exporting,
        selectedRows,
        tableRows,
        buildParams,
        designationWiseUsers,
        status,
        type,
        searchText,
        selectedLeadAM,
        selectedCategory
    ]);
    const handleIcpDraftChange = useCallback((draft) => {
        setIcpDrafts((prev) => ({
            ...prev,
            [draft.clientId]: {
                alias: draft.alias,
                sample_status: draft.sample_status,
                brand_description: draft.brand_description,
                changedFields: draft.changedFields,
                dirty: draft.dirty,
            },
        }));
    }, []);
    const handleIcpSaveFromToolbar = useCallback(async () => {
        if (icpSaving)
            return;
        const entries = Object.entries(icpDrafts).filter(([, draft]) => draft && draft.dirty);
        if (!entries.length)
            return;
        setIcpSaving(true);
        try {
            const updates = entries.map(([clientId, draft]) => ({
                clientId,
                fields: draft.changedFields || {
                    alias: draft.alias,
                    sample_status: draft.sample_status,
                    brand_description: draft.brand_description,
                },
            }));
            await dispatch(bulkUpdateClientIcpFields({ updates })).unwrap();
            setIcpDrafts((prev) => {
                const next = { ...prev };
                entries.forEach(([clientId]) => {
                    if (next[clientId]) {
                        next[clientId] = { ...next[clientId], dirty: false };
                    }
                });
                return next;
            });
        }
        catch {
            // errors surfaced via thunk; do not change UI here
        }
        finally {
            setIcpSaving(false);
        }
    }, [dispatch, icpDrafts, icpSaving]);
    const hasPendingIcpUpdates = useMemo(() => Object.values(icpDrafts).some((d) => d.dirty), [icpDrafts]);
    /* ─── edit handlers ─── */
    const handleEditClick = useCallback(async (client) => {
        setEditingClient(client);
        setEditDrawerOpen(true);
        try {
            const result = await dispatch(fetchClientDetails({ clientId: client._id }));
            if (fetchClientDetails.fulfilled.match(result)) {
                setClientEditData(result.payload);
            }
        }
        catch {
            /* no-op */
        }
    }, [dispatch]);
    const handleDrawerClose = useCallback(() => {
        setEditDrawerOpen(false);
        setEditingClient(null);
        setClientEditData(null);
    }, []);
    const handleAddNetworkOpen = useCallback((client) => {
        setNetworkClient(client);
        setNetworkDrawerOpen(true);
    }, []);
    /* ─── table columns ─── */
    const columns = useMemo(() => [
        { id: 'name', label: 'Client Name', width: '20%', align: 'left', sortable: true },
        { id: 'category', label: 'Category', width: '18%', align: 'left', sortable: true },
        { id: 'type', label: 'Type', width: '12%', align: 'left', sortable: true },
        { id: 'start_date', label: 'Start Date', width: '12%', align: 'left', sortable: true },
        { id: 'termination_date', label: 'Termination Date', width: '13%', align: 'left', sortable: true },
        { id: 'mrr', label: 'MRR', width: '12%', align: 'left', sortable: true },
        { id: 'commission', label: 'Commission', width: '11%', align: 'left', sortable: true },
    ], []);
    /* ─── cell renderer ─── */
    const renderCell = useCallback((row, column) => {
        const idx = clients.findIndex((c) => c._id === row._id);
        const color = getAvatarColor(idx >= 0 ? idx : 0);
        switch (column.id) {
            case 'name': {
                const nameVal = row.name;
                const nameEmpty = !nameVal || String(nameVal).trim().toLowerCase() === 'null';
                const displayName = nameEmpty ? '' : String(nameVal).trim();
                const contractTerm = row.icp?.contract_term?.toLowerCase() || '';
                let badgeText = '';
                let badgeTooltip = '';
                if (contractTerm === 'monthly') {
                    badgeText = 'M';
                    badgeTooltip = 'Monthly';
                }
                else if (contractTerm === '3 months') {
                    badgeText = '3M';
                    badgeTooltip = '3 Months';
                }
                else if (contractTerm === '6 months') {
                    badgeText = '6M';
                    badgeTooltip = '6 Months';
                }
                else if (contractTerm === 'yearly' || contractTerm === 'annual' || contractTerm === 'annually') {
                    badgeText = 'Y';
                    badgeTooltip = 'Yearly';
                }
                return (_jsx(Box, { className: 'user-table-name-cell', sx: { cursor: 'default', display: 'flex', alignItems: 'center', gap: 1 }, title: displayName, children: _jsxs(Box, { className: 'user-table-name-wrapper', sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { className: 'user-table-name-text', children: displayName }), badgeText && (_jsx(CommonChip, { renderType: "circle", label: badgeText, tooltip: badgeTooltip }))] }) }));
            }
            case 'category': {
                const cat = row.icp?.category;
                const isEmpty = !cat || String(cat).trim().toLowerCase() === 'null';
                const catText = isEmpty ? '' : String(cat).trim();
                return isEmpty ? null : (_jsx(HoverTooltip, { title: catText, placement: "top", children: _jsx(Box, { className: 'type-badge', sx: { display: 'inline-flex', maxWidth: '160px', overflow: 'hidden' }, children: _jsx(Typography, { component: 'span', sx: {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }, children: catText }) }) }));
            }
            case 'type': {
                const types = Array.isArray(row.type)
                    ? row.type
                    : row.type
                        ? [row.type]
                        : [];
                if (!types.length) {
                    return _jsx(Typography, { className: 'user-table-email-text', component: 'span' });
                }
                // When multiple Type filters are active, keep listing consistent
                // by showing the unified multi-service badge.
                if (type.length > 1) {
                    return (_jsx(CommonChip, { label: `${type.length}+ Services`, renderType: "tag", className: "active-client-type-badge active-client-type-badge--multi", sx: {
                            // padding: '2px 10px',
                            fontWeight: 500,
                            background: '#FFF4ED',
                            color: '#C4320A',
                        }, tooltip: _jsx(Box, { sx: { minWidth: 140 }, children: type.map((t, idx) => (_jsx(Box, { sx: {
                                    py: 1,
                                    fontSize: '12px',
                                    color: '#FFFFFF',
                                    textTransform: 'capitalize',
                                    borderBottom: idx !== type.length - 1
                                        ? '1px solid rgba(255,255,255,0.4)'
                                        : 'none',
                                }, children: TYPE_LABEL_MAP[t] ?? t }, t || idx))) }) }));
                }
                // Single type → show as colored tag chip (same as previous TypeBadge)
                if (types.length === 1) {
                    const t = types[0];
                    const variantClass = t === 'affiliate'
                        ? 'active-client-type-badge active-client-type-badge--affiliate'
                        : t === 'paid_media'
                            ? 'active-client-type-badge active-client-type-badge--paid-media'
                            : 'active-client-type-badge';
                    const colorSx = t === 'affiliate'
                        ? { background: '#F4F3FF', color: '#5925DC' }
                        : t === 'paid_media'
                            ? { background: '#E0F2FF', color: '#1D4ED8' }
                            : {};
                    return (_jsx(CommonChip, { label: TYPE_LABEL_MAP[t] ?? t, renderType: "tag", className: variantClass, sx: {
                            padding: '2px 10px',
                            fontSize: '14px',
                            fontWeight: 500,
                            ...colorSx,
                        } }));
                }
                // Multiple types → show "+N Services" with tooltip list (same colors as previous multi badge)
                return (_jsx(CommonChip, { label: `${types.length}+ Services`, renderType: "tag", className: "active-client-type-badge active-client-type-badge--multi", sx: {
                        padding: '2px 10px',
                        fontWeight: 500,
                        background: '#FFF4ED',
                        color: '#C4320A',
                    }, tooltip: _jsx(Box, { sx: {
                            background: '#0B0F19',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            minWidth: 180,
                        }, children: types.map((t, idx) => (_jsx(Box, { sx: {
                                px: 2,
                                py: 1.2,
                                fontSize: '12px',
                                color: '#FFFFFF',
                                textTransform: 'capitalize',
                                borderBottom: idx !== types.length - 1
                                    ? '1px solid rgba(255,255,255,0.4)'
                                    : 'none',
                            }, children: TYPE_LABEL_MAP[t] ?? t }, t || idx))) }) }));
            }
            case 'start_date':
                return (_jsx(Typography, { className: 'user-table-email-text', children: formatDate(row.start_date) }));
            case 'termination_date':
                return (_jsx(Typography, { className: 'user-table-email-text', children: formatDate(row.termination_date) }));
            case 'mrr':
                return (_jsx(Typography, { className: 'user-table-email-text', component: 'span', children: row.icp?.contract_mrr != null ? (_jsxs(_Fragment, { children: [_jsx(Box, { component: 'span', sx: { color: '#A1A1AA' }, children: "$ " }), Number(row.icp.contract_mrr).toLocaleString('en-US', { minimumFractionDigits: 2 })] })) : ('') }));
            case 'commission': {
                const raw = row.icp?.contract_commission_rate;
                let displayNum = raw != null
                    ? String(raw).replace(/%\s*$/, '').replace(/\$\s*/g, '').trim()
                    : '';
                if (displayNum.toLowerCase() === 'null')
                    displayNum = '';
                return (_jsx(Typography, { className: 'user-table-email-text', component: 'span', children: displayNum !== '' ? (_jsxs(_Fragment, { children: [displayNum, _jsx(Box, { component: 'span', sx: { color: '#A1A1AA' }, children: " %" })] })) : ('') }));
            }
            default:
                return null;
        }
    }, [clients, type]);
    /* ─── expanded row renderer ─── */
    const renderExpandedContent = useCallback((row) => (_jsx(ExpandedRow, { client: row, onEdit: handleEditClick, onAddNetwork: handleAddNetworkOpen, onIcpDraftChange: handleIcpDraftChange, canManage: hasPermission && status === 'active' })), [handleEditClick, handleAddNetworkOpen, handleIcpDraftChange, hasPermission, status]);
    /* ─── ui ─── */
    return (_jsxs(PageContainer, { children: [_jsx(Box, { className: 'user-list-container active-client-list-module', children: _jsxs(Paper, { className: 'user-list-card', elevation: 0, children: [_jsxs(Box, { className: 'user-list-header', children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '8px', mb: 1.5 }, children: [_jsx(PagesCommonHeader, { height: '58px', title: 'Active Client List', rightSection: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 }, children: [_jsx(Dropdown, { value: type, onChange: handleTypeChange, options: typeOptions, placeholder: "Type: All", displayValue: typeDisplayValue, leftIcon: "/assets/TypeC.svg", multiple: true, size: "sm", fullWidth: false, className: "header-dropdown" }), _jsx(Dropdown, { value: selectedCategory, onChange: (val) => {
                                                            setSelectedCategory(val);
                                                            setPage(1);
                                                        }, options: categoryOptions, placeholder: 'Category All', displayValue: categoryDisplayValue, leftIcon: '/assets/CategoryC.svg', size: 'sm', fullWidth: false, className: 'header-dropdown', usePortal: true }), _jsx(Dropdown, { value: selectedContractTerm, onChange: (val) => {
                                                            setSelectedContractTerm(val);
                                                            setPage(1);
                                                        }, options: contractTermOptions, placeholder: 'Contract Term All', displayValue: contractTermDisplayValue, size: 'sm', fullWidth: false, className: 'header-dropdown', usePortal: true }), _jsx(Dropdown, { value: selectedLeadAM, onChange: (val) => {
                                                            setSelectedLeadAM(val);
                                                            setPage(1);
                                                        }, options: amOptions, placeholder: 'Lead Account Manager: All', displayValue: amDisplayValue, leftIcon: '/assets/LeadAmC.svg', size: 'sm', fullWidth: false, className: 'header-dropdown', searchable: accountManagers.length > 5, usePortal: true })] }) }), _jsx(PagesCommonHeader, { height: '65px', lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: lastUpdated, showIcon: canRefreshData, onRefresh: handleSyncRefresh }), rightSection: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(Box, { sx: { width: 260 }, children: _jsx(SearchInput, { value: searchText, onSearch: handleSearchChange, placeholder: 'Search By Client Name' }) }), _jsx(ExportButton, { onClick: handleExportAllClients, loading: exporting })] }) })] }), _jsxs(Box, { className: 'user-list-header-content', sx: { backgroundColor: 'white', padding: '10px', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', border: '1px solid #E5E7EB', borderBottom: 'none' }, children: [_jsx(CommonTabs, { selectedValue: status, onChange: (val) => { setStatus(val); setPage(1); }, tabs: [
                                                {
                                                    value: 'active',
                                                    label: (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(Box, { className: "user-list-tab-dot user-list-tab-dot-active", sx: { margin: 0 } }), "Active (", counts.active, " Clients)"] }))
                                                },
                                                {
                                                    value: 'inactive',
                                                    label: (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(Box, { className: "user-list-tab-dot user-list-tab-dot-inactive", sx: { margin: 0 } }), "Inactive (", counts.inactive, " Clients)"] }))
                                                }
                                            ] }), _jsxs(Box, { className: 'user-list-toolbar-actions', children: [hasPermission && status === 'active' && (hasPendingIcpUpdates || icpSaving) && (_jsx(AppButton, { variant: 'contained', onClick: handleIcpSaveFromToolbar, disabled: icpSaving || !hasPendingIcpUpdates, sx: {
                                                        height: 40,
                                                        px: 3,
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        bgcolor: '#FF008E',
                                                        border: '1px solid #A3005C',
                                                        '&:hover': {
                                                            bgcolor: '#e0007c',
                                                        },
                                                        '&.Mui-disabled': {
                                                            backgroundColor: '#F3F4F6',
                                                            color: '#9CA3AF',
                                                            borderColor: '#E5E7EB',
                                                        },
                                                    }, className: 'all-Font-Sizes', children: "Update" })), _jsx(TableToolbar, { title: '', showSearch: false, showRows: false, showExpand: true, showFullScreen: true, onSearchChange: handleSearchChange, searchValue: searchText, enableColumnVisibility: true, onColumnVisibilityClick: (event) => setColumnVisibilityAnchor(event.currentTarget), enableColumnFilters: false, showFilterRow: showFilterRow, onFilterRowToggle: () => {
                                                        handleFilterRowToggle({ mode: 'all' });
                                                    }, customActions: null })] })] })] }), _jsx(Box, { className: 'user-list-table-wrapper', children: _jsx(CommonTable, { columns: columns, rows: tableRows, renderCell: renderCell, renderExpandedContent: renderExpandedContent, expandTrigger: 'icon', sortConfig: sortConfig, onSortChange: (columnId, direction) => {
                                    setSortConfig(columnId && direction
                                        ? { columnId, direction }
                                        : { columnId: null, direction: 'asc' });
                                    if (columnId && direction)
                                        setPage(CLIENT_PAGINATION.DEFAULT_PAGE);
                                }, type: 'userlist-table active-client-table', isLoading: loading || icpSaving || refreshing, isFirstLoad: true, enableSelection: true, selectedRows: selectedRows, onSelectionChange: setSelectedRows, columnVisibilityConfig: {
                                    enable: true,
                                    columns: ['name', 'category', 'type', 'start_date', 'termination_date', 'mrr', 'commission', 'actions'],
                                    enableReorder: false,
                                }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), enableColumnActions: true, enableColumnPinning: true, pinnedColumns: pinnedColumns, onColumnPinningChange: setPinnedColumns, enableColumnFilters: false, showFilterRow: showFilterRow, activeFilterColumnId: activeFilterColumnId, onActiveFilterColumnIdChange: setActiveFilterColumnId, columnFilters: columnFilters, onColumnFilterChange: handleColumnFilterChange, onFilterRowToggle: handleFilterRowToggle, enableColumnReorder: false, columnOrder: columnOrder, onColumnOrderChange: handleColumnOrderChange, columnFilterConfig: columnFilterConfig }) }), !loading && totalEntries > 0 && (_jsx(Box, { className: 'user-list-pagination-wrapper', sx: { paddingInline: '14px' }, children: _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, onPageChange: (newPage) => setPage(newPage), maxVisiblePages: 5, totalEntries: totalEntries, itemsPerPage: CLIENT_PAGINATION.LIMIT, showEntriesMessage: true }) }))] }) }), _jsx(AddEditClientDrawer, { open: addDrawerOpen, mode: 'create', onClose: () => setAddDrawerOpen(false), onSuccess: refetchClientList, status: status }), editDrawerOpen && editingClient && (_jsx(AddEditClientDrawer, { open: editDrawerOpen, mode: 'edit', clientId: editingClient._id, clientData: clientEditData, onClose: handleDrawerClose, onSuccess: refetchClientList, status: status })), _jsx(AddNetworkDrawer, { open: networkDrawerOpen, onClose: () => setNetworkDrawerOpen(false), client: networkClient, onSuccess: refetchClientList, canManage: hasPermission && status === 'active' })] }));
}
